<template>
  <dialog ref="dialog" :class="[dialogClass]" @cancel="handleDialogCancel">
    <div class="dialog-content">
      <div class="dialog-header">
        <slot name="header">
          <span class="title-dialog">{{ title }}</span>
        </slot>
        <mc-button v-if="showCloseButton" class="close-button" @click="closeDialog" icon="icon-close-cross-exit-quit-delete"/>
      </div>
      <div class="dialog-body">
        <slot></slot>
      </div>
      <div class="dialog-footer">
        <slot name="actions">
          <div v-if="showActionButtons" class="actions">
            <mc-button class="dialog-button" :class="['cancel-button', cancelButtonClass]" @click="handleCancelButtonClick">
              {{cancelButtonText}}
            </mc-button>
            <mc-button class="dialog-button" :disabled="submitDisabled" :class="['submit-button', submitButtonClass]" @click="handleSubmitButtonClick">
              {{submitButtonText}}
            </mc-button>
          </div>
        </slot>
      </div>
    </div>
  </dialog>
</template>

<script lang="ts" setup >
import { ref, toRefs } from 'vue';
import McButton from '@/components/ui/McButton.vue';

interface IMcDialogProps {
  title?: string;
  dialogClass?: string;
  showActionButtons?: boolean;
  showCloseButton?: boolean;
  submitButtonText?: string;
  submitButtonClass?: string;
  cancelButtonText?: string;
  cancelButtonClass?: string;
  closeBySubmitClick?: boolean;
  closeByCancelClick?: boolean;
  // Esc does what the Cancel button does: closes, unless closeByCancelClick is off, and emits cancel.
  // Off by default, since a dialog's Cancel button isn't always one Esc should press.
  cancelOnEscape?: boolean;
  submitDisabled? : boolean;
};

const props = withDefaults(defineProps<IMcDialogProps>(), {
  title: "",
  dialogClass: "",
  showActionButtons: true,
  showCloseButton: false,
  submitButtonText: "Save",
  submitButtonClass: "",
  cancelButtonText: "Close",
  cancelButtonClass: "",
  closeBySubmitClick: true,
  closeByCancelClick: true,
  cancelOnEscape: false,
  submitDisabled: false
});

const {
  title, 
  dialogClass,
  submitButtonText, 
  submitButtonClass,
  cancelButtonText,
  cancelButtonClass,
  closeBySubmitClick,
  closeByCancelClick,
  cancelOnEscape,
  submitDisabled } = toRefs(props);

const dialog = ref<HTMLDialogElement | null>(null)

const emit = defineEmits(['submit', 'cancel']);

const handleSubmitButtonClick = () => {
  if (closeBySubmitClick.value) closeDialog();
  emit('submit');
};

const handleCancelButtonClick = () => {
  if (closeByCancelClick.value) closeDialog();
  emit('cancel');
};

// Esc fires cancel on the <dialog>, which then closes by itself unless the event is prevented.
// A file input inside fires a bubbling cancel too, when its picker is dismissed: not Esc.
const handleDialogCancel = (event: Event) => {
  if (!cancelOnEscape.value || event.target !== event.currentTarget) return;

  event.preventDefault();
  handleCancelButtonClick();
};

function openDialog() {
  dialog.value?.showModal();
};

function closeDialog() {
  dialog.value?.close();
}

defineExpose({
  openDialog,
  closeDialog
});
</script>

<style>
:root {
  --dialog-width: 475px;
  --dialog-height: fit-content;
  --dialog-max-height: 85vh;
  --dialog-background-color: var(--colour-surface-overlay);
  --dialog-box-shadow: 0px 2px 14px 0px rgba(3, 20, 71, 0.30);
  --dialog-border: none;
  --dialog-border-radius: 8px;
  --dialog-padding: 36px 32px 24px 32px;
  --dialog-content-height: 100%;
  --dialog-content-width: 100%;
  --dialog-actions-section-gap: 8px;
  --dialog-actions-section-margin: 16px 0 0 0;
  --dialog-action-buttons-padding: 11px 20px;
  --dialog-cancel-button-width: auto;
  --dialog-submit-button-width: auto;
}
</style>

<style scoped lang="scss">
dialog {
  width: var(--dialog-width);
  height: var(--dialog-height);
  min-width: var(--dialog-min-width);
  max-height: var(--dialog-max-height);
  background: var(--dialog-background-color);
  box-shadow: var(--dialog-box-shadow);
  padding: var(--dialog-padding);
  border-radius: var(--dialog-border-radius);
  border: var(--dialog-border);

  &::backdrop {
    background: rgba(0, 0, 0, 0.50);
    z-index: 2700;
  }
}

.dialog-content {
  display: flex;
  flex-direction: column;
  height: var(--dialog-content-height);
  width: var(--dialog-content-width);
  border-radius: var(--dialog-border-radius);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-body {
  flex: 1;
  overflow-y: auto;
}

.dialog-footer {
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--dialog-actions-section-gap);
    margin: var(--dialog-actions-section-margin);

    .dialog-button {
      --button-padding: var(--dialog-action-buttons-padding);
    }

    .cancel-button {
      --button-width: var(--dialog-cancel-button-width);
    }

    .submit-button {
      --button-width: var(--dialog-submit-button-width);
    }
  }
}
</style>